import { Routes, Route, Navigate, useLocation, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { editorialLanding, sectionForPath } from './lib/sections'
import { operatorMayOpen } from './lib/navGroups'
import { editorDeskRedirect, editorMayOpen } from './lib/deskAccess'
import { EditorialLayout } from './editorial/EditorialLayout'
import { EditorOnly } from './editorial/EditorOnly'
import { TitleChooser } from './editorial/TitleChooser'
import { CheckSourcesPage } from './editorial/CheckSourcesPage'
import { EditorialTitle } from './editorial/EditorialTitle'
import { deskHref } from './editorial/links'
import { ClassicView } from './editorial/ClassicView'
import { DeskPage } from './editorial/DeskPage'
import { PiecesPage } from './editorial/PiecesPage'
import { PiecePage } from './editorial/PiecePage'
import { RidesDeskPage } from './editorial/rides/RidesDeskPage'
import { RideReviewPage } from './editorial/rides/RideReviewPage'
import { ImportsPage } from './editorial/ImportsPage'
import { ImportPage } from './editorial/ImportPage'
import { FromWordPressPage } from './editorial/FromWordPressPage'
import { WeeklyPanel } from './editorial/weekly/WeeklyPanel'
import { StartPiecePage } from './editorial/StartPiecePage'
import { MakeVersionPage } from './editorial/MakeVersionPage'
import { IdeasPage } from './editorial/IdeasPage'
import { SchedulePage } from './editorial/SchedulePage'
import { LivePage } from './editorial/LivePage'
import { SocialPage } from './editorial/SocialPage'
import { TopicPagesPage as EditorialTopicPagesPage } from './editorial/TopicPagesPage'
import { TrackFinderPage } from './editorial/TrackFinder'
import { SettingsPage as EditorialSettingsPage } from './editorial/SettingsPage'
import { ManualPage as EditorialManualPage } from './editorial/ManualPage'
import { DeskLayout } from './desk/DeskLayout'
import { ActivityPage } from './desk/ActivityPage'
import { SearchPage } from './desk/SearchPage'
import { MemberPage } from './desk/MemberPage'
import { PaymentPage } from './desk/PaymentPage'
import { PrintPage } from './desk/PrintPage'
import { BatchPage } from './desk/BatchPage'
import { DeskManualPage } from './desk/DeskManualPage'
import { CampaignDeskLayout } from './campaigns-desk/CampaignDeskLayout'
import { ListPage as CampaignDeskListPage } from './campaigns-desk/ListPage'
import { NewBriefPage as CampaignDeskNewPage } from './campaigns-desk/NewBriefPage'
import { CampaignPage as CampaignDeskCampaignPage } from './campaigns-desk/CampaignPage'
import { SettingsPage as CampaignDeskSettingsPage } from './campaigns-desk/SettingsPage'
import { AccountingLayout } from './accounting/AccountingLayout'
import { ClosesPage } from './accounting/ClosesPage'
import { ClosePage } from './accounting/ClosePage'
import { AccountsPage } from './accounting/AccountsPage'
import { HistoryPage } from './accounting/HistoryPage'
import { PrintClosePage } from './accounting/PrintClosePage'
import { AppShell } from './layouts/AppShell'
import { LoginPage } from './pages/Login'
import { DashboardPage } from './pages/Dashboard'
import { SubscribersPage } from './pages/Subscribers'
import { SubscriberDetailPage } from './pages/SubscriberDetail'
import { TestPoolsPage } from './pages/TestPools'
import { TestPoolDetailPage } from './pages/TestPoolDetail'
import { ARReportPage } from './pages/ARReport'
import { ARReportCohortPage } from './pages/ARReportCohort'
import { ARReportBucketPage } from './pages/ARReportBucket'
import { ARReportAttemptsPage } from './pages/ARReportAttempts'
import { ARReportAttemptDetailPage } from './pages/ARReportAttemptDetail'
import { SegmentsPage } from './pages/Segments'
import { TitleDetailPage } from './pages/TitleDetail'
import { AggregateDetailPage } from './pages/AggregateDetail'
import { SegmentBuilderPage } from './pages/SegmentBuilder'
import { EventsPage } from './pages/Events'
import { EmailsLogPage } from './pages/EmailsLog'
import { ContactMessagesPage } from './pages/ContactMessages'
import { FeedbackMessagesPage } from './pages/FeedbackMessages'
import { EmailMessagesPage } from './pages/EmailMessages'
import { UnthreadedRepliesPage } from './pages/UnthreadedReplies'
import { ReplyDeskGuidePage } from './pages/ReplyDeskGuide'
import { KnownIssuesPage } from './pages/KnownIssues'
import { SyncPage } from './pages/Sync'
import { DuplicatesPage } from './pages/Duplicates'
import { TemplatesPage } from './pages/Templates'
import { TemplateEditorPage } from './pages/TemplateEditor'
import { CampaignsPage } from './pages/Campaigns'
import { CampaignEditorPage } from './pages/CampaignEditor'
import { CampaignResultsPage } from './pages/CampaignResults'
import { IssueAnnouncementsPage } from './pages/IssueAnnouncements'
import { MailingsPage } from './pages/Mailings'
import { MailingResultsPage } from './pages/MailingResults'
import { NewslettersPage } from './pages/Newsletters'
import { NewsletterSettingsPage } from './pages/NewsletterSettings'
import { NewsletterSubscribersPage } from './pages/NewsletterSubscribers'
import { LabArchivePage } from './lab/LabArchive'
import { LabIssuesPage } from './lab/LabIssues'
import { LabIssueViewPage } from './lab/LabIssueView'
import { LabArchiveArticleViewPage } from './lab/LabArchiveArticleView'
import { LabContentSelectorPage } from './lab/LabContentSelector'
import { LabCampaignDryRunPage } from './lab/LabCampaignDryRun'
import { LabTemplatesPage } from './lab/LabTemplates'
import { LabTemplateEditorPage } from './lab/LabTemplateEditor'
import { PilotFunnelPage } from './pages/PilotFunnel'
import { TrailersPage } from './pages/Trailers'
import { MarketingLandingPage } from './pages/MarketingLanding'
import { SettingsPage } from './pages/Settings'
import { UsersPage } from './pages/Users'
import { TopicPagesPage } from './pages/TopicPages'
import { SectionsPage } from './pages/SectionsPage'
import { AgentProposalsPage } from './pages/AgentProposals'
import { PromoCodesPage } from './pages/PromoCodes'
import { SiteOrdersPage } from './pages/SiteOrders'
import { RenewalsPage } from './pages/Renewals'
import { VisitorsOverview } from './pages/visitors/VisitorsOverview'
import { VisitorsTitle } from './pages/visitors/VisitorsTitle'
import { DeskTabPage } from './pages/visitors/desk/DeskTabPage'
import { DeskArticle } from './pages/visitors/desk/DeskArticle'
import { DeskIssues } from './pages/visitors/desk/DeskIssues'
import { SummaryPage } from './pages/visitors/desk/agent/SummaryPage'
import { DeskSettings } from './pages/visitors/desk/DeskSettings'
import { VisitorsDeskLayout } from './pages/visitors/desk/VisitorsDeskLayout'
import { ManualPage as VisitorsManualPage } from './pages/visitors/desk/ManualPage'
import { VisitorsFunnelStep } from './pages/visitors/VisitorsFunnelStep'
import { VisitorsBreakdown } from './pages/visitors/VisitorsBreakdown'
import { VisitorsSessions } from './pages/visitors/VisitorsSessions'
import { VisitorsSession } from './pages/visitors/VisitorsSession'
import { VisitorsEvent } from './pages/visitors/VisitorsEvent'
import { VisitorsAtRisk } from './pages/visitors/VisitorsAtRisk'
import { VisitorsMember } from './pages/visitors/VisitorsMember'
import { VisitorsVisitor } from './pages/visitors/VisitorsVisitor'
import { VisitorsDownloads } from './pages/visitors/VisitorsDownloads'
import { PrintOrdersPage } from './pages/PrintOrders'
import { SiteOrderDetailPage } from './pages/SiteOrderDetail'
import { AddCompPage } from './pages/AddComp'
import { OfflineIntakePage } from './pages/OfflineIntake'
import { SupportOverview } from './pages/support/SupportOverview'
import { SupportTitle } from './pages/support/SupportTitle'
import { SupportSetup } from './pages/support/SupportSetup'
import { SavedRepliesPage } from './pages/support/SavedReplies'
import { api, ApiError, type MeResponse } from './api/client'

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/visitors/desk"
        element={
          <RequireAuth>
            <VisitorsDeskLayout />
          </RequireAuth>
        }
      >
        <Route index element={<Navigate to="all/today" replace />} />
        <Route path="settings" element={<DeskSettings />} />
        <Route path="help" element={<VisitorsManualPage />} />
        <Route path=":pub/articles/:articleId" element={<DeskArticle />} />
        <Route path=":pub/issues/:issueId" element={<DeskIssues />} />
        <Route path=":pub/summary" element={<SummaryPage />} />
        <Route path=":pub/:tab" element={<DeskTabPage />} />
      </Route>
      <Route
        path="/"
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="support" element={<SupportOverview />} />
        <Route path="support/setup" element={<SupportSetup />} />
        <Route path="support/saved-replies" element={<SavedRepliesPage />} />
        <Route path="support/:pub" element={<SupportTitle />} />
        <Route path="visitors" element={<VisitorsOverview />} />
        <Route path="visitors/:pub" element={<VisitorsTitle />} />
        <Route path="visitors/:pub/funnel/:step" element={<VisitorsFunnelStep />} />
        <Route path="visitors/:pub/breakdown/:dimension" element={<VisitorsBreakdown />} />
        <Route path="visitors/:pub/sessions" element={<VisitorsSessions />} />
        <Route path="visitors/:pub/sessions/:sessionId" element={<VisitorsSession />} />
        <Route path="visitors/:pub/events/:eventId" element={<VisitorsEvent />} />
        <Route path="visitors/:pub/at-risk" element={<VisitorsAtRisk />} />
        <Route path="visitors/:pub/downloads" element={<VisitorsDownloads />} />
        <Route path="visitors/:pub/members/:subscriberId" element={<VisitorsMember />} />
        <Route path="visitors/:pub/visitors/:visitorId" element={<VisitorsVisitor />} />
        <Route path="aggregate" element={<AggregateDetailPage />} />
        <Route path="titles/:slug" element={<TitleDetailPage />} />
        <Route path="subscribers" element={<SubscribersPage />} />
        <Route path="orders" element={<SiteOrdersPage />} />
        <Route path="renewals" element={<RenewalsPage />} />
        <Route path="orders/:id" element={<SiteOrderDetailPage />} />
        <Route path="print-orders" element={<PrintOrdersPage />} />
        <Route path="offline-intake" element={<OfflineIntakePage />} />
        <Route path="comps/new" element={<AddCompPage />} />
        <Route path="subscribers/test-pools" element={<TestPoolsPage />} />
        <Route path="subscribers/test-pools/:id" element={<TestPoolDetailPage />} />
        <Route path="ar-report" element={<ARReportPage />} />
        <Route path="ar-report/:month/:publication" element={<ARReportCohortPage />} />
        <Route path="ar-report/:month/:publication/bucket/:bucket" element={<ARReportBucketPage />} />
        <Route path="ar-report/:month/:publication/attempts" element={<ARReportAttemptsPage />} />
        <Route path="ar-report/attempt/:id" element={<ARReportAttemptDetailPage />} />
        <Route path="subscribers/:id" element={<SubscriberDetailPage />} />
        <Route path="segments" element={<SegmentsPage />} />
        <Route path="segments/with-email" element={<SegmentsPage mode="with-email" />} />
        <Route path="segments/without-email" element={<SegmentsPage mode="without-email" />} />
        <Route path="segments/:id" element={<SegmentBuilderPage />} />
        <Route path="templates" element={<TemplatesPage />} />
        <Route path="templates/:id" element={<TemplateEditorPage />} />
        <Route path="campaigns" element={<CampaignsPage />} />
        <Route path="campaigns/:id" element={<CampaignEditorPage />} />
        <Route path="campaigns/:id/results" element={<CampaignResultsPage />} />
        <Route path="issue-announcements" element={<IssueAnnouncementsPage />} />
        <Route path="issue-announcements/:editionId" element={<IssueAnnouncementsPage />} />
        <Route path="mailings" element={<MailingsPage />} />
        <Route path="mailings/:id" element={<MailingResultsPage />} />
        <Route path="newsletters" element={<NewslettersPage />} />
        <Route path="newsletters/:pub/subscribers" element={<NewsletterSubscribersPage />} />
        <Route path="newsletters/:pub/settings" element={<NewsletterSettingsPage />} />
        <Route path="events" element={<EventsPage />} />
        <Route path="emails" element={<EmailsLogPage />} />
        <Route path="contact" element={<ContactMessagesPage />} />
        <Route path="feedback" element={<FeedbackMessagesPage />} />
        <Route path="email" element={<EmailMessagesPage />} />
        <Route path="unthreaded" element={<UnthreadedRepliesPage />} />
        <Route path="help/reply-desk" element={<ReplyDeskGuidePage />} />
        <Route path="known-issues" element={<KnownIssuesPage />} />
        <Route path="sync" element={<SyncPage />} />
        <Route path="duplicates" element={<DuplicatesPage />} />
        <Route path="lab/archive" element={<LabArchivePage />} />
        <Route path="lab/archive/:id" element={<LabArchiveArticleViewPage />} />
        <Route path="lab/issues" element={<LabIssuesPage />} />
        <Route path="lab/issues/:id" element={<LabIssueViewPage />} />
        <Route path="lab/content" element={<LabContentSelectorPage />} />
        <Route path="lab/dry-run" element={<LabCampaignDryRunPage />} />
        <Route path="lab/templates" element={<LabTemplatesPage />} />
        <Route path="lab/templates/:id" element={<LabTemplateEditorPage />} />
        <Route path="track-a/pilot-funnel" element={<PilotFunnelPage />} />
        <Route path="lab/trailers" element={<TrailersPage />} />
        <Route path="marketing-landing" element={<MarketingLandingPage />} />
        <Route path="promo-codes" element={<PromoCodesPage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="topic-pages" element={<TopicPagesPage />} />
        <Route path="sections" element={<SectionsPage />} />
        <Route path="public-content/:path" element={<LegacyPublicContent />} />
        <Route path="section-drafts" element={<Navigate to="/editorial/flight-line" replace />} />
        <Route path="agent-proposals" element={<AgentProposalsPage />} />
      </Route>
      <Route
        path="/desk"
        element={
          <RequireAuth>
            <DeskLayout />
          </RequireAuth>
        }
      >
        <Route index element={<ActivityPage />} />
        <Route path="find" element={<SearchPage />} />
        <Route path="members/:id" element={<MemberPage />} />
        <Route path="members/:id/pay" element={<PaymentPage />} />
        <Route path="members/:id/print/:what" element={<PrintPage />} />
        <Route path="batch" element={<BatchPage />} />
        <Route path="batch/:id" element={<BatchPage />} />
        <Route path="help" element={<DeskManualPage />} />
        <Route path="*" element={<Navigate to="/desk" replace />} />
      </Route>
      <Route
        path="/campaigns-desk"
        element={
          <RequireAuth>
            <CampaignDeskLayout />
          </RequireAuth>
        }
      >
        <Route index element={<CampaignDeskListPage />} />
        <Route path="new" element={<CampaignDeskNewPage />} />
        <Route path="c/:id" element={<CampaignDeskCampaignPage />} />
        <Route path="settings" element={<CampaignDeskSettingsPage />} />
        <Route path="*" element={<Navigate to="/campaigns-desk" replace />} />
      </Route>
      <Route
        path="/accounting"
        element={
          <RequireAuth>
            <AccountingLayout />
          </RequireAuth>
        }
      >
        <Route index element={<ClosesPage />} />
        <Route path="accounts" element={<AccountsPage />} />
        <Route path="history" element={<HistoryPage />} />
        <Route path=":slug/:period" element={<ClosePage />} />
        <Route path=":slug/:period/print" element={<PrintClosePage />} />
        <Route path="*" element={<Navigate to="/accounting" replace />} />
      </Route>
      <Route
        path="/editorial"
        element={
          <RequireAuth>
            <EditorialLayout />
          </RequireAuth>
        }
      >
        <Route index element={<TitleChooser />} />
        <Route path="help" element={<EditorialManualPage />} />
        <Route path=":path" element={<EditorialTitle />}>
          <Route index element={<DeskPage />} />
          <Route path="pieces" element={<PiecesPage />} />
          <Route path="pieces/:id" element={<PiecePage />} />
          <Route path="pieces/:id/sources" element={<CheckSourcesPage />} />
          <Route path="imports" element={<ImportsPage />} />
          <Route path="imports/:importId" element={<ImportPage />} />
          <Route path="from-wordpress" element={<EditorOnly><FromWordPressPage /></EditorOnly>} />
          <Route path="start" element={<StartPiecePage />} />
          <Route path="version" element={<MakeVersionPage />} />
          <Route path="ideas" element={<IdeasPage />} />
          <Route path="schedule" element={<EditorOnly><SchedulePage /></EditorOnly>} />
          <Route path="weekly" element={<WeeklyPanel />} />
          <Route path="live" element={<LivePage />} />
          <Route path="social" element={<EditorOnly><SocialPage /></EditorOnly>} />
          <Route path="topics" element={<EditorOnly><EditorialTopicPagesPage /></EditorOnly>} />
          <Route path="tracks" element={<TrackFinderPage />} />
          <Route path="settings" element={<EditorOnly><EditorialSettingsPage /></EditorOnly>} />
          <Route path="readers-rides" element={<RidesDeskPage />} />
          <Route path="readers-rides/:id" element={<RideReviewPage />} />
          <Route path="classic" element={<AdminOnlyClassic />} />
          <Route path="*" element={<Navigate to=".." relative="path" replace />} />
        </Route>
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  )
}

/** The old Schedule & settings page: nothing links here any more. Editors go to the Desk; admins may still open it by URL. */
function AdminOnlyClassic() {
  const { path } = useParams()
  const { data: me, isLoading } = useQuery({ queryKey: ['me'], queryFn: () => api.get<MeResponse>('/api/me'), retry: false, staleTime: 60_000 })
  if (isLoading) return null
  if (me?.role !== 'admin') return <Navigate to={deskHref(path ?? '')} replace />
  return <ClassicView />
}

function LegacyPublicContent() {
  const { path } = useParams()
  return <Navigate to={`/editorial/${path ?? ''}`} replace />
}

function RequireAuth({ children }: { children: React.ReactNode }) {
  const loc = useLocation()
  const { data: me, isLoading, error } = useQuery({
    queryKey: ['me'],
    queryFn: () => api.get<MeResponse>('/api/me'),
    retry: false,
    staleTime: 60_000,
  })

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <span className="label text-paper-trace animate-blink">LOADING</span>
      </div>
    )
  }
  if (error instanceof ApiError && error.status === 401) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />
  }
  if (!me) {
    return <Navigate to="/login" state={{ from: loc.pathname }} replace />
  }
  // An editor or writer account lives in Editorial Desk and nowhere else.
  if (me.role === 'editor' || me.role === 'writer') {
    const landing = editorialLanding(me.sections ?? [])
    const inEditorial = loc.pathname === '/editorial' || loc.pathname.startsWith('/editorial/')
    if (!inEditorial && !editorMayOpen(loc.pathname, me)) {
      // Old /public-content/<path> links keep their title when the editor may open it.
      const legacy = loc.pathname.match(/^\/public-content\/([^/]+)\/?$/)
      if (legacy && sectionForPath(me.sections ?? [], legacy[1])) return <Navigate to={`/editorial/${legacy[1]}`} replace />
      // A refused desk URL (another title, a tab they don't have) goes to their own Articles.
      const desk = editorDeskRedirect(loc.pathname, me)
      if (desk) return <Navigate to={desk} replace />
      return <Navigate to={landing} replace />
    }
    if (!(me.sections ?? []).length && loc.pathname !== '/editorial/none') return <Navigate to="/editorial/none" replace />
  }
  // A Subscriber Desk account lives at /desk and nowhere else.
  if (me.role === 'desk' && !(loc.pathname === '/desk' || loc.pathname.startsWith('/desk/'))) return <Navigate to="/desk" replace />
  // An Accounting account lives at /accounting and nowhere else.
  if (me.role === 'accounting' && !(loc.pathname === '/accounting' || loc.pathname.startsWith('/accounting/'))) return <Navigate to="/accounting" replace />
  // An operator account is Customer Support staff: the desk and its pages, nowhere else.
  if (me.role === 'operator' && !operatorMayOpen(loc.pathname)) return <Navigate to="/support" replace />
  return <>{children}</>
}

function NotFound() {
  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <div className="font-serif text-6xl text-amber-glow">404</div>
        <div className="label mt-4">Signal lost</div>
      </div>
    </div>
  )
}
